<template>
  <div class="aigame-container">
    <!-- 遊戲開始前:學生資訊輸入 -->
    <div v-if="beforeGame" class="before-game-container">
      <div v-if="isFromActId" class="actid-mode-wrapper">
        <div class="actid-mode-modal">
          <!-- 左側:遊戲資訊和播放按鈕 -->
          <div class="game-info-left">
            <div class="game-title">{{ gameConfig.topic || '自定義遊戲' }}</div>
            <div class="game-info">
              <div class="game-type">{{ $t('gameActManager.aiGame') }}</div>
            </div>
            <!-- 按鈕區 -->
            <div class="left-buttons">
              <button class="action-button play-button play-in-left" @click="startGame">
                <svg-icon icon-class="play" class="play-icon" />
              </button>
            </div>
          </div>

          <!-- 右側:學生資訊輸入 -->
          <div class="student-info-right">
            <h4 class="student-info-title">{{ $t('gameActManager.enterStudentInfo') }}</h4>
            <div class="form-group">
              <input
                v-model="studentInfo.seatNumber"
                type="text"
                :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                maxlength="3"
                class="student-input"
              />
              <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
            </div>
            <div class="form-group">
              <input
                v-model="studentInfo.name"
                type="text"
                :placeholder="$t('gameActManager.studentNamePlaceholder')"
                maxlength="20"
                class="student-input"
              />
              <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 預覽模式:直接開始 -->
      <div v-else class="preview-mode-wrapper">
        <div class="preview-modal">
          <h2>{{ $t('gameActManager.previewMode') }}</h2>
          <p>{{ gameConfig.topic || '自定義遊戲預覽' }}</p>
          <button class="start-button" @click="startGame">
            {{ $t('gameActManager.confirmStudentInfo') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 遊戲進行中:載入 iframe -->
    <div v-else class="game-iframe-container">
      <iframe
        ref="gameIframe"
        :srcdoc="gameHtml"
        sandbox="allow-scripts"
        class="game-iframe"
        @load="onIframeLoad"
      ></iframe>

      <!-- 成績排行榜 Modal（疊在 iframe 上方） -->
      <div v-if="showScoresModal" class="scores-overlay">
        <div class="scores-modal">
          <div class="scores-header">
            <span class="scores-title">🏆 成績排行榜</span>
            <button class="scores-close" @click="showScoresModal = false">✕</button>
          </div>
          <div v-if="myScore" class="my-score-row">
            我的得分：<strong>{{ myScore.score }} / {{ myScore.maxScore }}</strong>
          </div>
          <div class="scores-table-wrapper">
            <table class="scores-table">
              <thead>
                <tr>
                  <th>名次</th>
                  <th>座號</th>
                  <th>姓名</th>
                  <th>得分</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(s, i) in scoresData"
                  :key="i"
                  :class="{ 'my-row': s.studentName === studentInfo.name && s.seatNumber === studentInfo.seatNumber }"
                >
                  <td>{{ i + 1 }}</td>
                  <td>{{ s.seatNumber }}</td>
                  <td>{{ s.studentName }}</td>
                  <td>{{ s.score }} / {{ s.maxScore }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '../components/SvgIcon.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';

const { t } = useI18n();
const route = useRoute();

// 狀態
const beforeGame = ref(true);
const gameHtml = ref('');
const gameConfig = ref({
  topic: '',
  customCode: ''
});
const savedParams = ref(null); // 從雲端載入或由遊戲回傳儲存的設定

// 學生資訊
const studentInfo = ref({
  name: '',
  seatNumber: ''
});
const studentInfoErrors = ref({
  name: '',
  seatNumber: ''
});

// 成績排行榜
const showScoresModal = ref(false);
const scoresData = ref([]);
const myScore = ref(null);

// 判斷是否從 actId 進入
const isFromActId = computed(() => {
  return !!route.query.actId;
});

// 判斷是否在 HiTeach WebView 內
const isInWebView = computed(() => {
  return !!window?.chrome?.webview;
});

// 學生模式：有 actId 且不在 WebView（一般瀏覽器的學生連結）
const isStudentMode = computed(() => {
  return isFromActId.value && !isInWebView.value;
});

// 判斷是否為預覽模式
const isPreviewMode = computed(() => {
  const previewData = localStorage.getItem('game_preview_data');
  return !!previewData && !isFromActId.value;
});

// 注入學生模式腳本：
// 1. <head> 設定 window.name（供新遊戲讀取）
// 2. </body> 前覆寫 showScreen（每次切到 home-screen 時隱藏設定按鈕，向下相容舊遊戲）
const injectStudentMode = (html) => {
  if (!html) return html;

  const headScript = `<script>window.name="student";<\/script>`;

  const bodyScript = `<script>
(function(){
  function hideSettingsBtn(){
    var home=document.getElementById('home-screen');
    if(!home)return;
    home.querySelectorAll('button,a').forEach(function(el){
      var oc=(el.getAttribute('onclick')||'').toLowerCase();
      var tx=(el.textContent||'').toLowerCase();
      if(oc.includes('setting')||tx.includes('設定')||tx.includes('setting')){
        el.style.display='none';
      }
    });
  }
  if(typeof showScreen==='function'){
    var _orig=showScreen;
    showScreen=function(id){_orig(id);if(id==='home-screen')hideSettingsBtn();};
  }
  var h=document.getElementById('home-screen');
  if(h&&h.style.display!=='none')hideSettingsBtn();
})();
<\/script>`;

  let result = html;
  result = result.includes('<head>')
    ? result.replace('<head>', '<head>' + headScript)
    : headScript + result;

  result = result.includes('</body>')
    ? result.replace('</body>', bodyScript + '</body>')
    : result.includes('</html>')
      ? result.replace('</html>', bodyScript + '</html>')
      : result + bodyScript;

  return result;
};

// Refs
const gameIframe = ref(null);

// 驗證學生資訊
const validateStudentInfo = () => {
  studentInfoErrors.value = { name: '', seatNumber: '' };
  let isValid = true;

  // 驗證姓名
  if (!studentInfo.value.name.trim()) {
    studentInfoErrors.value.name = t('gameActManager.studentNameRequired');
    isValid = false;
  } else if (studentInfo.value.name.length > 20) {
    studentInfoErrors.value.name = t('gameActManager.studentNameTooLong');
    isValid = false;
  }

  // 驗證座號（必填）
  if (!studentInfo.value.seatNumber.trim()) {
    studentInfoErrors.value.seatNumber = t('gameActManager.seatNumberRequired');
    isValid = false;
  } else {
    const seatNum = parseInt(studentInfo.value.seatNumber);
    if (isNaN(seatNum) || seatNum < 1 || seatNum > 999) {
      studentInfoErrors.value.seatNumber = t('gameActManager.seatNumberInvalid');
      isValid = false;
    }
  }

  return isValid;
};

// 開始遊戲
const startGame = () => {
  // 如果是正式活動,需要驗證學生資訊
  if (isFromActId.value) {
    if (!validateStudentInfo()) {
      return;
    }
  }

  // 隱藏開始畫面,顯示遊戲
  beforeGame.value = false;
};

// iframe 載入完成：若有儲存的 params，送給遊戲預填
const onIframeLoad = () => {
  console.log('[AI Game] iframe 載入完成');
  if (savedParams.value && gameIframe.value?.contentWindow) {
    gameIframe.value.contentWindow.postMessage({
      action: 'Init.AIGame',
      data: savedParams.value
    }, '*');
  }
};

// 監聽遊戲訊息：SaveParams.AIGame（存設定）+ Submit.AIGame（存成績並顯示排行榜）
const setupMessageListener = () => {
  window.addEventListener('message', async (event) => {
    const msg = event.data;

    // ── SaveParams.AIGame：遊戲設定存回雲端 ─────────────────────
    if (msg?.action === 'SaveParams.AIGame' && msg.data && route.query.actId) {
      const newParams = msg.data;
      savedParams.value = newParams;
      try {
        const currentSettings = gameConfig.value._rawSettings || {};
        const updatedSettings = JSON.stringify({
          ...currentSettings,
          topic: newParams.topic || currentSettings.topic,
          params: newParams
        });
        await gameActivityAPI.updateGameActivity({
          id: route.query.actId,
          gameContentSettings: updatedSettings
        });
        console.log('[AI Game] params 已儲存到雲端');
      } catch (err) {
        console.error('[AI Game] 儲存 params 失敗:', err);
      }
    }

    // ── Submit.AIGame：成績存後端，取排行榜顯示 Modal ───────────
    if (msg?.action === 'Submit.AIGame' && msg.data && isStudentMode.value) {
      const { score, maxScore, completed, durationSeconds, ...extraFields } = msg.data;
      myScore.value = { score, maxScore };

      try {
        // 存成績（學生端只提交，不取排行榜—需老師權限）
        // correctCount / wrongCount：遊戲有帶就用，沒帶則預設 0
        await gameActivityAPI.submitScore({
          activityId: route.query.actId,
          studentName: studentInfo.value.name,
          seatNumber: studentInfo.value.seatNumber,
          score,
          maxScore,
          completed,
          durationSeconds,
          correctCount: extraFields.correctCount ?? 0,
          wrongCount: extraFields.wrongCount ?? 0,
          ...extraFields
        });
        console.log('[AI Game] 成績已提交');
      } catch (err) {
        console.error('[AI Game] 成績提交或排行榜取得失敗:', err);
      }
    }
  });
};

// 從 actId 載入遊戲活動
const loadActivityFromActId = async (actId) => {
  try {
    console.log('[AI Game] 載入活動:', actId);

    const response = await gameActivityAPI.getGameActivity({ id: actId });
    console.log('[AI Game] API 回應:', response);

    if (response && response.status === 200 && response.data) {
      const activityData = response.data;
      console.log('[AI Game] activityData:', activityData);

      // 檢查活動是否開放進入
      if (!activityData.enableEntry) {
        console.warn('[AI Game] 活動未開放進入');
        gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; flex-direction: column; color: #f56c6c;"><div style="font-size: 24px; margin-bottom: 16px;">⚠️</div><div>' + t('gameActManager.activityNotOpen') + '</div></div>';
        beforeGame.value = false;
        return;
      }

      // 解析 gameContentSettings (JSON 字串)
      let gameParameters = null;
      if (activityData.gameContentSettings) {
        try {
          gameParameters = JSON.parse(activityData.gameContentSettings);
          console.log('[AI Game] gameParameters:', gameParameters);
        } catch (e) {
          console.error('[AI Game] 解析 gameContentSettings 失敗:', e);
        }
      }

      // 設定遊戲配置
      gameConfig.value = {
        topic: gameParameters?.topic || activityData.activityName || '自定義遊戲',
        customCode: gameParameters?.customCode || '',
        _rawSettings: gameParameters // 保留原始設定，更新時合併用
      };

      // 若有之前儲存的 params，載入備用（onIframeLoad 時會注入給遊戲）
      if (gameParameters?.params) {
        savedParams.value = gameParameters.params;
        console.log('[AI Game] 載入已儲存的 params:', savedParams.value);
      }

      // 設定 gameHtml（學生模式注入隱藏設定按鈕邏輯）
      gameHtml.value = isStudentMode.value
        ? injectStudentMode(gameConfig.value.customCode)
        : gameConfig.value.customCode;

      console.log('[AI Game] 活動載入成功:', gameConfig.value.topic);
      console.log('[AI Game] customCode 長度:', gameConfig.value.customCode.length);

      if (!gameConfig.value.customCode) {
        console.warn('[AI Game] customCode 是空的！');
        gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">遊戲代碼為空</div>';
      }
    } else {
      console.error('[AI Game] 活動資料格式錯誤，response:', response);
      gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">載入遊戲失敗：資料格式錯誤</div>';
    }
  } catch (error) {
    console.error('[AI Game] 載入活動失敗:', error);
    gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">載入遊戲失敗：' + error.message + '</div>';
  }
};

// 從 localStorage 載入預覽資料
const loadPreviewData = () => {
  const previewData = localStorage.getItem('game_preview_data');

  if (previewData) {
    try {
      const data = JSON.parse(previewData);

      if (data.gameParameters && data.gameParameters.customCode) {
        gameConfig.value = {
          topic: data.gameParameters.topic || '自定義遊戲預覽',
          customCode: data.gameParameters.customCode
        };

        gameHtml.value = isStudentMode.value
          ? injectStudentMode(gameConfig.value.customCode)
          : gameConfig.value.customCode;

        console.log('[AI Game] 預覽資料載入成功');
      }
    } catch (error) {
      console.error('[AI Game] 解析預覽資料失敗:', error);
    }
  }
};

// 初始化 Auth Token
const initializeAuth = async () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_token_expiry");
  try {
    const res = await api.main.getTokenUnlogin();
    const exp = jwt_decode(res.auth_token).exp;
    localStorage.setItem("auth_token", res.auth_token);
    localStorage.setItem("auth_token_expiry", exp);
    console.log('[AI Game] Auth token 初始化成功');
  } catch (error) {
    console.error("[AI Game] Auth token 初始化失敗:", error);
  }
};

// 初始化
onMounted(async () => {
  console.log('[AI Game] 頁面初始化');
  console.log('[AI Game] Route query:', route.query);

  // 監聽遊戲送回的設定
  setupMessageListener();

  // 判斷載入來源
  if (route.query.actId) {
    // 從 actId 載入 - 先初始化 auth_token
    await initializeAuth();
    loadActivityFromActId(route.query.actId);
  } else {
    // 從 localStorage 載入預覽資料
    loadPreviewData();
  }
});
</script>

<style lang="less" scoped>
@import "../assets/gameActManager.less";

.aigame-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #f5f5f5;
}

// 遊戲開始前
.before-game-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

// 正式活動模式
.actid-mode-wrapper {
  width: 90%;
  max-width: 900px;
}

.actid-mode-modal {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 30px 20px;
  }
}

.game-info-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.game-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
}

.game-info {
  margin-bottom: 30px;
}

.game-type {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.left-buttons {
  width: 100%;
}

.play-button {
  width: 100%;
  padding: 16px;
  font-size: 18px;
  font-weight: 600;
  background: @game-primary-color;
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: @game-hover-color;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }

  .play-icon {
    width: 24px;
    height: 24px;
  }
}

// 學生資訊輸入
.student-info-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.student-info-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.student-input {
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: @game-primary-color;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
}

.error-text {
  display: block;
  margin-top: 8px;
  font-size: 14px;
  color: #f56c6c;
}

// 預覽模式
.preview-mode-wrapper {
  width: 90%;
  max-width: 500px;
}

.preview-modal {
  background: white;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #333;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    color: #666;
    margin-bottom: 30px;
  }

  .start-button {
    width: 100%;
    padding: 16px;
    font-size: 18px;
    font-weight: 600;
    background: @game-primary-color;
    color: white;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: @game-hover-color;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }
  }
}

// 遊戲區域
.game-iframe-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.game-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

// 成績排行榜 Modal
.scores-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  backdrop-filter: blur(4px);
}

.scores-modal {
  background: #1a1a2e;
  border: 1px solid #0f3460;
  border-radius: 20px;
  padding: 28px 24px;
  width: 90%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  color: #fff;
}

.scores-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.scores-title {
  font-size: 20px;
  font-weight: 700;
}

.scores-close {
  background: none;
  border: none;
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  &:hover { color: #fff; }
}

.my-score-row {
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid #4caf50;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 15px;
  margin-bottom: 14px;
  color: #8fe28f;
}

.scores-table-wrapper {
  overflow-y: auto;
  flex: 1;
}

.scores-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    color: #aaa;
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #0f3460;
  }

  td {
    padding: 10px;
    border-bottom: 1px solid #0f3460;
  }

  .my-row {
    background: rgba(76, 175, 80, 0.1);
    td { color: #8fe28f; }
  }

  tr:last-child td { border-bottom: none; }
}
</style>
